import { createEffect, createEvent, createStore, sample, split } from "effector";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IAmplitudeFlagsContract } from "@features/amplitude-flags/model";
import { IMetadataContract } from "@features/student/metadata/model";
import { IUserAuthContract } from "@features/student/auth/model";
import { IProductOnboardingContract } from "@features/onboarding/ui/productOnboarding";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import {
    trackAppTourCompleted,
    trackAppTourLeftPath,
    trackAppTourRestored,
    trackAppTourResynced,
    trackAppTourSkipped,
    trackAppTourStarted
} from "@features/shared/events";
import { getAppTourSteps, setAppTourRoadmapCertificate, AppTourRoute, toAppTourRoute } from "../steps";

export const APP_TOUR_FINISHED_KEY = "appTourFinished";
const APP_TOUR_STEP_STORAGE_KEY = "app_tour_step";

// The tour is for NEW users only: someone who finished the product onboarding
// within this window. Existing users (e.g. returning through the renew
// paywall) carry an old productOnboardingFinished timestamp and must never get
// the tour dropped on top of their flow.
const ONBOARDING_FRESHNESS_MS = 24 * 60 * 60 * 1000;

const isTourFinished = (metadata: Record<string, unknown> | null): boolean =>
    localStorage.getItem(APP_TOUR_FINISHED_KEY) !== null || metadata?.[APP_TOUR_FINISHED_KEY] !== undefined;

/** Mid-tour progress saved for this tab session; null = none or invalid. */
const readSavedStepIndex = (): number | null => {
    if (typeof window === "undefined") return null;
    try {
        const raw = sessionStorage.getItem(APP_TOUR_STEP_STORAGE_KEY);
        if (raw === null) return null;
        const index = Number(raw);
        return Number.isInteger(index) && index >= 0 && index < getAppTourSteps().length ? index : null;
    } catch {
        return null;
    }
};

type AppTourLaunchSource = {
    student: unknown;
    flagsLoaded: boolean;
    appTourEnabled: boolean | null;
    metadata: Record<string, unknown> | null;
    metadataLoading: boolean;
    metadataInitialized: boolean;
    isProductOnboardingModalOpen: boolean;
    isProductV3OnboardingActive: boolean;
    isHomeActive: boolean;
    tourActive: boolean;
    lastRoute: AppTourRoute | null;
};

// The tour launches once: right after the product onboarding is finished, on
// the first visit to /home. Mirrors shouldOpenProductOnboardingModal's data
// readiness checks, but with the inverse onboarding condition.
const isEligibleToStartAppTour = ({
    student,
    flagsLoaded,
    metadata,
    metadataLoading,
    metadataInitialized,
    isProductOnboardingModalOpen,
    isProductV3OnboardingActive,
    isHomeActive
}: AppTourLaunchSource): boolean => {
    if (typeof window === "undefined") return false;
    if (student === null) return false;
    if (!flagsLoaded) return false;
    if (!metadataInitialized || metadataLoading) return false;
    if (isProductOnboardingModalOpen || isProductV3OnboardingActive) return false;
    if (!isHomeActive) return false;

    // The stored value is the finish moment (new Date().toISOString(), see
    // handleLessonExit). Unparseable legacy values read as "old user": skip.
    const finishedAtRaw =
        localStorage.getItem("productOnboardingFinished") ?? metadata?.productOnboardingFinished;
    if (finishedAtRaw === null || finishedAtRaw === undefined) return false;
    const finishedAt = Date.parse(String(finishedAtRaw));
    if (!Number.isFinite(finishedAt)) return false;
    if (Date.now() - finishedAt > ONBOARDING_FRESHNESS_MS) return false;

    return !isTourFinished(metadata);
};

// A reload mid-tour restores the saved step instead of starting over — but
// only when the tab landed back on one of the tour's own screens.
const canRestoreAppTour = ({
    student,
    flagsLoaded,
    metadata,
    metadataLoading,
    metadataInitialized,
    lastRoute
}: AppTourLaunchSource): boolean => {
    if (typeof window === "undefined") return false;
    if (student === null) return false;
    if (!flagsLoaded) return false;
    if (!metadataInitialized || metadataLoading) return false;
    if (lastRoute === null) return false;
    if (readSavedStepIndex() === null) return false;

    return !isTourFinished(metadata);
};

const tourStarted = createEvent();
const nextStepRequested = createEvent();
const prevStepRequested = createEvent();
export const tourSkipped = createEvent();
const tourCompleted = createEvent();
const tourFinished = createEvent<string>(); // ISO timestamp to persist

// Fired (from run()) whenever one of the tour's three screens opens. Guided
// navigation advances the tour here — whether the user tapped the highlighted
// tab through the spotlight hole or pressed "Show me" on the sheet.
const routeOpened = createEvent<AppTourRoute>();

type RouteOpenedAction = {
    action: "advance" | "resync" | "abandon" | "ignore";
    index: number;
    fromStepId: string;
    toStepId: string | null;
    route: AppTourRoute;
};

const stepAdvancedByNavigation = createEvent<RouteOpenedAction>();
const stepResynced = createEvent<RouteOpenedAction>();
const tourAbandoned = createEvent<RouteOpenedAction>();
const tourRestored = createEvent<number>();

// The last tour screen the router opened — the restore flow re-syncs the
// saved step against it.
const $lastRoute = createStore<AppTourRoute | null>(null).on(routeOpened, (_, route) => route);

export const $tourActive = createStore(false)
    .on(tourStarted, () => true)
    .on(tourRestored, () => true)
    .on(tourFinished, () => false);

export const $stepIndex = createStore(0)
    .on(tourStarted, () => 0)
    .on(nextStepRequested, (index) => Math.min(index + 1, getAppTourSteps().length - 1))
    // Index 0 is the welcome modal — there is no way back into it.
    .on(prevStepRequested, (index) => Math.max(index - 1, 1))
    .on([stepAdvancedByNavigation, stepResynced], (_, { index }) => index)
    .on(tourRestored, (_, index) => index);

const $currentStep = $stepIndex.map((index) => getAppTourSteps()[index] ?? null);

const trackTourStartedFx = createEffect(() => {
    trackAppTourStarted(getAppTourSteps().length);
});

const trackTourSkippedFx = createEffect((stepId: string) => {
    trackAppTourSkipped(stepId);
});

const trackTourCompletedFx = createEffect(() => {
    trackAppTourCompleted();
});

const trackTourResyncedFx = createEffect(({ fromStepId, toStepId, route }: RouteOpenedAction) => {
    trackAppTourResynced(fromStepId, toStepId ?? "", route);
});

const trackTourLeftPathFx = createEffect(({ fromStepId, route }: RouteOpenedAction) => {
    trackAppTourLeftPath(fromStepId, route);
});

const trackTourRestoredFx = createEffect((index: number) => {
    trackAppTourRestored(getAppTourSteps()[index]?.id ?? "unknown");
});

const persistTourFinishedFx = createEffect((finishedAt: string) => {
    if (typeof window === "undefined") return;
    localStorage.setItem(APP_TOUR_FINISHED_KEY, finishedAt);
});

const persistStepIndexFx = createEffect((index: number) => {
    try {
        sessionStorage.setItem(APP_TOUR_STEP_STORAGE_KEY, String(index));
    } catch {
        // locked-down webviews can make Storage throw — progress just won't survive a reload
    }
});

const applyRoadmapCertificateFlagFx = createEffect((enabled: boolean) => {
    setAppTourRoadmapCertificate(enabled);
});

const clearStepIndexFx = createEffect(() => {
    try {
        sessionStorage.removeItem(APP_TOUR_STEP_STORAGE_KEY);
    } catch {
        // ignore, see persistStepIndexFx
    }
});

// Landing on a screen mid-tour either advances (guided navigation), re-syncs to
// the first remaining step of that screen (deep link, back button), or — when
// no step lives there — ends the tour so no dimmed screen is left behind.
const routeActionComputed = sample({
    clock: routeOpened,
    source: { active: $tourActive, index: $stepIndex },
    filter: ({ active }) => active,
    fn: ({ index }, route): RouteOpenedAction => {
        const steps = getAppTourSteps();
        const step = steps[index];
        if (!step) return { action: "ignore", index, fromStepId: "unknown", toStepId: null, route };
        if (step.navigatesTo === route) {
            const next = steps[index + 1];
            return { action: "advance", index: index + 1, fromStepId: step.id, toStepId: next?.id ?? null, route };
        }
        if (step.kind === "modal" || step.route === route) {
            return { action: "ignore", index, fromStepId: step.id, toStepId: null, route };
        }
        const resyncIndex = steps.findIndex((s, i) => i >= index && s.kind !== "modal" && s.route === route);
        if (resyncIndex === -1) return { action: "abandon", index, fromStepId: step.id, toStepId: null, route };
        return {
            action: "resync",
            index: resyncIndex,
            fromStepId: step.id,
            toStepId: steps[resyncIndex].id,
            route
        };
    }
});

split({
    source: routeActionComputed,
    match: ({ action }: RouteOpenedAction) => action,
    cases: {
        advance: stepAdvancedByNavigation,
        resync: stepResynced,
        abandon: tourAbandoned
    }
});

sample({
    clock: stepResynced,
    target: trackTourResyncedFx
});

sample({
    clock: tourAbandoned,
    target: trackTourLeftPathFx
});

sample({
    clock: tourAbandoned,
    fn: () => new Date().toISOString(),
    target: tourFinished
});

sample({
    clock: tourStarted,
    target: trackTourStartedFx
});

sample({
    clock: tourRestored,
    target: trackTourRestoredFx
});

// The saved step may not live on the screen the tab reloaded into (guided
// navigation half-done, deep link) — run it through the same route logic as a
// live navigation: advance, re-sync or abandon.
sample({
    clock: tourRestored,
    source: $lastRoute,
    filter: (route): route is AppTourRoute => route !== null,
    target: routeOpened
});

sample({
    clock: tourStarted,
    fn: () => 0,
    target: persistStepIndexFx
});

sample({
    clock: $stepIndex,
    filter: $tourActive,
    target: persistStepIndexFx
});

sample({
    clock: tourFinished,
    target: clearStepIndexFx
});

sample({
    clock: tourSkipped,
    source: $currentStep,
    filter: $tourActive,
    fn: (step) => step?.id ?? "unknown",
    target: trackTourSkippedFx
});

// The last step's primary CTA completes the tour.
sample({
    clock: nextStepRequested,
    source: $stepIndex,
    filter: (index) => index >= getAppTourSteps().length - 1,
    target: tourCompleted
});

sample({
    clock: [tourSkipped, tourCompleted],
    filter: $tourActive,
    fn: () => new Date().toISOString(),
    target: tourFinished
});

// Matches the prototype: every way out of the tour fires app_tour_completed
// (skip/left-path additionally fire their own event just before).
sample({
    clock: tourFinished,
    target: trackTourCompletedFx
});

sample({
    clock: tourFinished,
    target: persistTourFinishedFx
});

const contract = new ModelContract(
    {
        $tourActive,
        $stepIndex,
        $currentStep
    },
    {
        nextStepRequested,
        prevStepRequested,
        tourSkipped
    },
    {},
    {}
);

export type IAppTourContract = typeof contract;

export class AppTourModule extends ModuleDescription<IAppTourContract> {
    constructor(
        public authModule: IUserAuthContract,
        public appRoutesModule: IAppRouteUnitsContract,
        public metadataModule: IMetadataContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract,
        public productOnboardingModule: IProductOnboardingContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        const launchSource = {
            student: this.authModule.stores.$student,
            flagsLoaded: this.amplitudeFlagsModule.stores.$flagsLoaded,
            appTourEnabled: this.amplitudeFlagsModule.stores.$appTourEnabled,
            metadata: this.metadataModule.stores.$metadata,
            metadataLoading: this.metadataModule.stores.$metadataLoading,
            metadataInitialized: this.metadataModule.stores.$metadataInitialized,
            isProductOnboardingModalOpen: this.productOnboardingModule.stores.$isProductOnboardingModalOpen,
            isProductV3OnboardingActive: this.productOnboardingModule.stores.$isProductV3OnboardingActive,
            isHomeActive: this.appRoutesModule.stores.home.$isActive,
            tourActive: $tourActive,
            lastRoute: $lastRoute
        };

        const launchClock = [
            routeOpened,
            this.amplitudeFlagsModule.stores.$flagsLoaded,
            this.metadataModule.stores.$metadata,
            this.productOnboardingModule.stores.$isProductOnboardingModalOpen,
            this.amplitudeFlagsModule.stores.$appTourEnabled,
            // Late authentication (magic link in a fresh browser) must also
            // re-evaluate the launch conditions — nothing else re-fires then.
            this.authModule.stores.$student
        ];

        // The certificate blocks are behind a 50/50 flag: for the arm without
        // them the two certificate steps must not exist at all — no auto-skip
        // pauses, and the crumb honestly says 6. The roster is only updated
        // while the tour is idle, so step indices never shift mid-run.
        sample({
            clock: this.amplitudeFlagsModule.stores.$roadmapCertificateEnabled,
            source: $tourActive,
            filter: (tourActive) => !tourActive,
            fn: (_, enabled) => enabled === true,
            target: applyRoadmapCertificateFlagFx
        });

        // A fresh browser that authenticates late (the magic link from the
        // change-browser instructions) misses the home.opened metadata
        // refresh: that fetch ran before auth and failed, and nothing retries
        // it until the NEXT visit to /home — so the tour never launched on the
        // first one. Refetch once the student is known and metadata is still
        // empty.
        sample({
            clock: this.authModule.stores.$student,
            source: {
                metadata: this.metadataModule.stores.$metadata,
                metadataLoading: this.metadataModule.stores.$metadataLoading
            },
            filter: ({ metadata, metadataLoading }, student) =>
                student !== null && !metadataLoading && Object.keys(metadata ?? {}).length === 0,
            target: this.metadataModule.events.refreshMetadataRequested
        });

        // The flag is evaluated lazily so the Amplitude $exposure population is
        // exactly the users who met the launch conditions (see the flag model).
        sample({
            clock: launchClock,
            source: launchSource,
            filter: (source: AppTourLaunchSource) =>
                source.appTourEnabled === null &&
                (isEligibleToStartAppTour(source) || canRestoreAppTour(source)),
            target: this.amplitudeFlagsModule.events.appTourFlagRequested
        });

        // Saved progress takes precedence over a fresh start (the fresh-start
        // filter excludes it), so a reload never rewinds the tour to step 0.
        sample({
            clock: launchClock,
            source: launchSource,
            filter: (source: AppTourLaunchSource) =>
                source.appTourEnabled === true &&
                !source.tourActive &&
                readSavedStepIndex() === null &&
                isEligibleToStartAppTour(source),
            target: tourStarted
        });

        sample({
            clock: launchClock,
            source: launchSource,
            filter: (source: AppTourLaunchSource) =>
                source.appTourEnabled === true && !source.tourActive && canRestoreAppTour(source),
            fn: () => readSavedStepIndex() as number,
            target: tourRestored
        });

        // Same dual write as productOnboardingFinished: localStorage for this
        // device, student metadata so other devices never show the tour again.
        sample({
            clock: tourFinished,
            fn: (finishedAt) => ({ key: APP_TOUR_FINISHED_KEY, value: finishedAt }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            clock: [
                this.appRoutesModule.stores.home.opened,
                this.appRoutesModule.stores.library.opened,
                this.appRoutesModule.stores.aiTools.opened
            ],
            filter: (snapshot) => toAppTourRoute(snapshot.path) !== null,
            fn: (snapshot) => toAppTourRoute(snapshot.path) as AppTourRoute,
            target: routeOpened
        });
    }
}
